import { Box, Collapse } from '@ltvco/refresh-lib/theme';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useState } from 'react';

import {
  AccordionCard,
  AccordionCardButton,
  AccordionDetailList,
  Chevron,
  CompactFieldRowItem,
  ExpandPanelInner,
  ExpandRowPreview,
  ExpandSection,
  FieldList,
  FieldRowItem,
  FieldRowLabel,
  FieldRowValue,
  ItemTextStack,
  MetricCard,
  MetricsGrid,
  OwnerAvatar,
  OwnerCard,
  OwnerCardContent,
  OwnerCardHeader,
  OwnerCardName,
  OwnerReportBar,
  OwnerReportButton,
  OwnerReportText,
  RecordItem,
  ReportFieldRow,
  SectionDivider,
  SubsectionTitle,
  VStack,
} from '../../components';
import {
  formatCurrency,
  formatLongDate,
  formatMonthYear,
  getInitials,
  hasValue,
  titleCase,
} from '../utils';

import {
  KvSub,
  NextStepButton,
  NextStepsTitle,
  RecordItemBody,
  RecordItemDate,
  RecordItemHead,
  RecordItemMeta,
  RecordItemTitle,
  ResidentRow,
  ShowMoreButton,
  TaxTableCell,
  TaxTableGrid,
  TaxTableHeadCell,
  TaxTableWrapper,
} from './components';

import type {
  PropertyDeed,
  PropertyReportData,
  PropertyTaxAssessment,
  PropertyTaxBill,
} from 'components/DeepSearch/types';

const PLACEHOLDER = '–';

function stripCents(value: string): string {
  return value.replace(/\.\d+$/, '');
}

const money = (n?: number | null): string =>
  n == null ? '' : formatCurrency(String(n));

const moneyRange = (min?: number | null, max?: number | null): string => {
  const lo = money(min);
  const hi = money(max);
  if (lo && hi && lo !== hi) {
    return `${lo} – ${hi}`;
  }
  return lo || hi;
};

const money2 = (n?: number | null): string =>
  n == null
    ? ''
    : `$${n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const displayMoney = (n?: number | null): string => {
  const formatted = money2(n);
  return formatted || PLACEHOLDER;
};

const tableMoney = (n?: number | null): string => {
  if (n == null) {
    return PLACEHOLDER;
  }
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

interface TaxTableRow {
  year: number;
  propertyTax: number | null;
  land: number | null;
  improvements: number | null;
  assessed: number | null;
}

const buildTaxTableRows = (
  bills: PropertyTaxBill[],
  assessments: PropertyTaxAssessment[]
): TaxTableRow[] => {
  const years = new Set([
    ...bills.map((bill) => bill.year),
    ...assessments.map((assessment) => assessment.year),
  ]);

  return Array.from(years)
    .sort((a, b) => b - a)
    .map((year) => {
      const bill = bills.find((entry) => entry.year === year);
      const assessment = assessments.find((entry) => entry.year === year);

      return {
        year,
        propertyTax: bill?.amount ?? null,
        land: assessment?.land ?? null,
        improvements: assessment?.improvements ?? null,
        assessed: assessment?.total ?? null,
      };
    });
};

const TaxRecordsTable = ({
  bills,
  assessments,
}: {
  bills: PropertyTaxBill[];
  assessments: PropertyTaxAssessment[];
}) => {
  const rows = buildTaxTableRows(bills, assessments);

  if (rows.length === 0) {
    return null;
  }

  return (
    <TaxTableWrapper>
      <TaxTableGrid>
        <TaxTableHeadCell>Year</TaxTableHeadCell>
        <TaxTableHeadCell align="right">Property tax</TaxTableHeadCell>
        <TaxTableHeadCell separator align="right">
          Land
        </TaxTableHeadCell>
        <TaxTableHeadCell align="right">+ Improvements</TaxTableHeadCell>
        <TaxTableHeadCell align="right">= Assessed value</TaxTableHeadCell>

        {rows.map((row, index) => {
          const striped = index % 2 === 0;
          const improvementsMissing = row.improvements == null;

          return (
            <Box key={row.year} sx={{ display: 'contents' }} role="row">
              <TaxTableCell striped={striped}>{row.year}</TaxTableCell>
              <TaxTableCell striped={striped} align="right">
                {row.propertyTax == null
                  ? PLACEHOLDER
                  : money2(row.propertyTax)}
              </TaxTableCell>
              <TaxTableCell striped={striped} separator align="right">
                {tableMoney(row.land)}
              </TaxTableCell>
              <TaxTableCell
                striped={striped}
                muted={improvementsMissing}
                align="right"
              >
                {improvementsMissing
                  ? PLACEHOLDER
                  : tableMoney(row.improvements)}
              </TaxTableCell>
              <TaxTableCell striped={striped} align="right">
                {tableMoney(row.assessed)}
              </TaxTableCell>
            </Box>
          );
        })}
      </TaxTableGrid>
    </TaxTableWrapper>
  );
};

const SOURCE_LABELS: Record<string, string> = {
  ca: 'County assessor (CA)',
  arch: 'ARCH model',
  bk: 'BK model',
};

const sourceLabel = (source: string): string =>
  SOURCE_LABELS[source.toLowerCase()] ?? source;

const FieldRow = ({
  label,
  value,
  compact = false,
}: {
  label: string;
  value: string;
  compact?: boolean;
}) => {
  const missing = !hasValue(value);
  const RowItem = compact ? CompactFieldRowItem : FieldRowItem;

  return (
    <RowItem>
      <FieldRowLabel>{label}:</FieldRowLabel>
      <FieldRowValue missing={missing}>
        {missing ? PLACEHOLDER : value}
      </FieldRowValue>
    </RowItem>
  );
};

// START VALUATION_SUMMARY
export const ValuationSummarySection = ({
  data,
}: {
  data: PropertyReportData;
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((o) => !o);

  const bestEstimate = data.valuationEstimates?.[0];
  const mvRange =
    moneyRange(bestEstimate?.min, bestEstimate?.max) ||
    (hasValue(data.estimatedValue) ? data.estimatedValue : '');
  const mvDate = bestEstimate?.date ? formatMonthYear(bestEstimate.date) : '';

  const rental = data.rentalValuation;
  const rentRange = moneyRange(rental?.minValue, rental?.maxValue);
  const rentDate = rental?.date ? formatMonthYear(rental.date) : '';

  const lastSoldFormatted =
    data.lastSoldPrice != null ? money(data.lastSoldPrice) : '';
  const lastSoldDateFormatted = data.lastSoldDate
    ? formatLongDate(data.lastSoldDate)
    : '';

  const previewText = mvRange
    ? `Est. market value ${mvRange}`
    : rental?.value != null
    ? `Est. rent ${money(rental.value)}/mo`
    : lastSoldFormatted
    ? `Last sold ${lastSoldFormatted}`
    : '';

  const hasAny = mvRange || rentRange || lastSoldFormatted;
  if (!hasAny) {
    return null;
  }

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              fontWeight: 700,
              fontSize: 16,
              color: 'text.primary',
              lineHeight: 1.3,
            }}
          >
            Valuation detail
          </Box>
          {previewText && <ExpandRowPreview>{previewText}</ExpandRowPreview>}
        </Box>
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <MetricsGrid>
            {mvRange && (
              <MetricCard
                icon={<HomeIcon />}
                label="Est. market value"
                value={mvRange}
                sub={
                  <>
                    {mvDate && <span>Last updated {mvDate}</span>}
                    {bestEstimate?.source && (
                      <span>{sourceLabel(bestEstimate.source)}</span>
                    )}
                  </>
                }
              />
            )}
            {rentRange && (
              <MetricCard
                icon={<VpnKeyIcon />}
                label="Est. monthly rent"
                value={rentRange}
                sub={rentDate ? `Last updated ${rentDate}` : undefined}
              />
            )}
            {lastSoldFormatted && (
              <MetricCard
                icon={<LocalOfferIcon />}
                label="Last sold price"
                value={lastSoldFormatted}
                sub={
                  lastSoldDateFormatted
                    ? `Sold on ${lastSoldDateFormatted}`
                    : undefined
                }
              />
            )}
          </MetricsGrid>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

// START PROPERTY_DETAILS
export const DetailsSection = ({ data }: { data: PropertyReportData }) => {
  const beds = hasValue(data.beds) ? data.beds : '';
  const baths = hasValue(data.baths) ? data.baths : '';
  const yearBuilt = hasValue(data.yearBuilt) ? data.yearBuilt : '';
  const lotSize = hasValue(data.lotSqFt) ? `${data.lotSqFt} sq ft` : '';
  const ownerOccupied = hasValue(data.ownerOccupied) ? data.ownerOccupied : '';

  if (!beds && !baths && !yearBuilt && !lotSize && !ownerOccupied) {
    return null;
  }

  return (
    <>
      <SectionDivider />
      <SubsectionTitle>Property details</SubsectionTitle>
      <FieldList>
        <FieldRow label="Beds" value={beds} />
        <FieldRow label="Baths" value={baths} />
        <FieldRow label="Year built" value={yearBuilt} />
        <FieldRow label="Lot size" value={lotSize} />
        <FieldRow label="Owner-occupied" value={ownerOccupied} />
      </FieldList>
    </>
  );
};

// START CURRENT_OWNERS
export const CurrentOwnersSection = ({
  data,
  onSendMessage,
}: {
  data: PropertyReportData;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const owners =
    data.owners && data.owners.length > 0
      ? data.owners
      : hasValue(data.currentOwner)
      ? [{ name: data.currentOwner, first: '', last: '' }]
      : [];

  if (owners.length === 0) {
    return null;
  }

  const vesting = data.details?.ownerVesting ?? '';
  const ownerOccupied = data.ownerOccupied;

  return (
    <>
      <SectionDivider />
      <SubsectionTitle>Current owners</SubsectionTitle>
      {owners.map((owner, index) => {
        const name = titleCase(owner.name);
        const role =
          index === 0 && ownerOccupied?.toLowerCase() === 'yes'
            ? 'Primary owner · owner-occupied'
            : index === 0
            ? 'Primary owner'
            : 'Co-owner';
        const details: [string, string][] = [
          ['Vesting', vesting],
          ['Owner-occupied', ownerOccupied],
        ];
        return (
          <OwnerAccordion
            key={`${owner.name}-${index}`}
            initials={getInitials(owner.name)}
            name={name}
            role={role}
            details={details}
            onSendMessage={onSendMessage}
          />
        );
      })}
    </>
  );
};

const OwnerAccordion = ({
  initials,
  name,
  role,
  details,
  onSendMessage,
}: {
  initials: string;
  name: string;
  role: string;
  details: [string, string][];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const hasContent = details.some(([, v]) => v !== '');
  const isExpandable = hasContent || !!onSendMessage;
  const toggleOpen = () => setOpen((o) => !o);

  function handleSendMessage() {
    onSendMessage?.(`Run a person report on ${name}`, `Search for ${name}`);
  }

  return (
    <OwnerCard mb={1}>
      <OwnerCardHeader
        type="button"
        aria-expanded={isExpandable ? open : undefined}
        disabled={!isExpandable}
        onClick={isExpandable ? toggleOpen : undefined}
      >
        <OwnerAvatar>{initials}</OwnerAvatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <OwnerCardName sx={{ display: 'block' }}>{name}</OwnerCardName>
          {role && (
            <Box
              component="span"
              sx={{
                display: 'block',
                fontSize: 12.5,
                fontWeight: 400,
                color: 'text.secondary',
                mt: '2px',
              }}
            >
              {role}
            </Box>
          )}
        </Box>
        {isExpandable && <Chevron open={open} />}
      </OwnerCardHeader>
      {isExpandable && (
        <Collapse in={open}>
          <OwnerCardContent>
            {onSendMessage && (
              <OwnerReportBar type="button" onClick={handleSendMessage}>
                <OwnerReportText>
                  View a complete report for {name}
                </OwnerReportText>
                <OwnerReportButton>
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </OwnerReportButton>
              </OwnerReportBar>
            )}
            {hasContent && (
              <AccordionDetailList>
                {details.map(([label, value]) => (
                  <FieldRow key={label} label={label} value={value} compact />
                ))}
              </AccordionDetailList>
            )}
          </OwnerCardContent>
        </Collapse>
      )}
    </OwnerCard>
  );
};

// START RECORDS_AND_HISTORY
const RECORD_LIST_PREVIEW_COUNT = 5;

const ShowMoreToggle = ({
  expanded,
  hasMore,
  onToggle,
}: {
  expanded: boolean;
  hasMore: boolean;
  onToggle: () => void;
}) => {
  if (!hasMore) {
    return null;
  }

  return (
    <ShowMoreButton type="button" aria-expanded={expanded} onClick={onToggle}>
      {expanded ? 'Show less' : 'Show More'}
    </ShowMoreButton>
  );
};

const ResidentsList = ({
  residents,
  onSendMessage,
}: {
  residents: NonNullable<PropertyReportData['residents']>;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasMore = residents.length > RECORD_LIST_PREVIEW_COUNT;
  const shown = expanded
    ? residents
    : residents.slice(0, RECORD_LIST_PREVIEW_COUNT);

  return (
    <Box>
      <VStack gap={0}>
        {shown.map((resident) => {
          const name = titleCase(resident.name);
          return (
            <RecordItem
              key={`${resident.name}-${resident.currentAddress}`}
              surface="paper"
              role={onSendMessage ? 'button' : undefined}
              tabIndex={onSendMessage ? 0 : undefined}
              onClick={
                onSendMessage
                  ? () => {
                      onSendMessage(
                        `Run a person report on ${name}`,
                        `Search for ${name}`
                      );
                    }
                  : undefined
              }
              onKeyDown={
                onSendMessage
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSendMessage(
                          `Run a person report on ${name}`,
                          `Search for ${name}`
                        );
                      }
                    }
                  : undefined
              }
              sx={
                onSendMessage
                  ? {
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }
                  : undefined
              }
            >
              <ResidentRow>
                <OwnerAvatar>{getInitials(resident.name)}</OwnerAvatar>
                <ItemTextStack>
                  <OwnerCardName>{name}</OwnerCardName>
                  <ExpandRowPreview>{resident.currentAddress}</ExpandRowPreview>
                  <KvSub>
                    {`${resident.phones.length} phone${
                      resident.phones.length === 1 ? '' : 's'
                    } · last seen ${formatMonthYear(resident.lastSeenDate)}`}
                  </KvSub>
                </ItemTextStack>
              </ResidentRow>
            </RecordItem>
          );
        })}
      </VStack>
      <ShowMoreToggle
        expanded={expanded}
        hasMore={hasMore}
        onToggle={() => setExpanded((value) => !value)}
      />
    </Box>
  );
};

const DeedsList = ({ deeds }: { deeds: PropertyDeed[] }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMore = deeds.length > RECORD_LIST_PREVIEW_COUNT;
  const shown = expanded ? deeds : deeds.slice(0, RECORD_LIST_PREVIEW_COUNT);

  return (
    <Box>
      <VStack gap={0}>
        {shown.map((deed) => {
          const loan = deed.loans?.[0];

          const deedTypeLine = [deed.deedType, deed.transferType]
            .filter(Boolean)
            .map(titleCase)
            .join(' · ');

          const loanLine = loan
            ? [
                loan.loanAmount ? money(loan.loanAmount) : '',
                loan.loanType ? titleCase(loan.loanType) : '',
                loan.lenderType && loan.lender
                  ? `${titleCase(loan.lenderType)} (${titleCase(loan.lender)})`
                  : loan.lender
                  ? titleCase(loan.lender)
                  : '',
              ]
                .filter(Boolean)
                .join(' · ')
            : '';

          return (
            <RecordItem key={deed.documentNumber} surface="paper">
              <RecordItemHead>
                <RecordItemTitle>
                  {money(loan?.loanAmount) ||
                    money(deed.transferValue) ||
                    'Transfer'}
                </RecordItemTitle>
                <RecordItemDate>
                  {formatLongDate(deed.recordingDate)}
                </RecordItemDate>
              </RecordItemHead>
              <RecordItemBody>
                {deedTypeLine}
                {deed.buyers.length > 0 && (
                  <>
                    <br />
                    {`Buyer: ${deed.buyers.map(titleCase).join(', ')}`}
                  </>
                )}
                {deed.sellers.length > 0 && (
                  <>
                    <br />
                    {`Seller: ${deed.sellers.map(titleCase).join(', ')}`}
                  </>
                )}
                {loanLine && (
                  <>
                    <br />
                    {`Loan: ${loanLine}`}
                  </>
                )}
              </RecordItemBody>
            </RecordItem>
          );
        })}
      </VStack>
      <ShowMoreToggle
        expanded={expanded}
        hasMore={hasMore}
        onToggle={() => setExpanded((value) => !value)}
      />
    </Box>
  );
};

export const RecordsHistorySection = ({
  data,
  onSendMessage,
}: {
  data: PropertyReportData;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const details = data.details;
  const assessments = data.taxAssessments ?? [];
  const bills = data.taxBills ?? [];
  const permits = data.permits ?? [];
  const residents = data.residents ?? [];
  const deeds = data.deeds ?? [];

  const hasAnything =
    Boolean(details) ||
    data.taxesCount > 0 ||
    data.buildingPermitsCount > 0 ||
    data.residentsCount > 0 ||
    data.deedsCount > 0;
  if (!hasAnything) {
    return null;
  }

  const legalRows: [string, string][] = details
    ? [
        ['County', details.county ? `${details.county} County` : ''],
        ['APN', details.apn],
        ['Zoning', details.zoning],
        ['Land use', details.landUse],
        [
          'Block / Section',
          hasValue(details.block)
            ? `${details.block} / ${details.section}`
            : '',
        ],
        [
          'Lot size',
          details.lotSizeSqFt
            ? `${details.lotSizeSqFt.toLocaleString()} sq ft`
            : '',
        ],
        ['Frontage', details.frontageFt ? `${details.frontageFt} ft` : ''],
        ['Depth', details.depthFt ? `${details.depthFt} ft` : ''],
        [
          'Building area',
          details.buildingAreaSqFt
            ? `${details.buildingAreaSqFt.toLocaleString()} sq ft`
            : '',
        ],
        ['Stories', details.stories ? String(details.stories) : ''],
        ['Units', details.units ? String(details.units) : ''],
        ['Vesting', details.ownerVesting],
      ]
    : [];

  const legalPreview = details
    ? [
        details.county ? `${details.county} County` : '',
        details.zoning ? `zoning ${details.zoning}` : '',
        details.units ? `${details.units} units` : '',
        details.stories ? `${details.stories} stories` : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const latestAssessment = assessments[0];
  const latestBill = bills[0];

  return (
    <>
      <SectionDivider />
      <SubsectionTitle>Records &amp; history</SubsectionTitle>

      <VStack gap={8}>
        {details && (
          <ExpandSection
            label="Property & legal details"
            preview={legalPreview || undefined}
          >
            <FieldList>
              {legalRows.map(([label, value]) => (
                <ReportFieldRow key={label} label={label} value={value} />
              ))}
            </FieldList>
          </ExpandSection>
        )}

        {data.taxesCount > 0 && (latestAssessment || latestBill) && (
          <ExpandSection
            label="Tax records"
            count={bills.length}
            preview={`${latestBill?.year ?? ''} tax ${stripCents(
              data.annualTax
            )} · assessed ${data.assessedValue}`}
          >
            {latestAssessment && (
              <MetricsGrid>
                <MetricCard
                  label={`Land value (${latestAssessment.year})`}
                  value={displayMoney(latestAssessment.land)}
                />
                <MetricCard
                  label={`Improvements (${latestAssessment.year})`}
                  value={displayMoney(latestAssessment.improvements)}
                />
                <MetricCard
                  label="Total value assessed"
                  value={displayMoney(latestAssessment.total)}
                />
                {latestBill && (
                  <MetricCard
                    label="Property taxes"
                    value={displayMoney(latestBill.amount)}
                  />
                )}
              </MetricsGrid>
            )}
            {bills.length > 0 && (
              <TaxRecordsTable bills={bills} assessments={assessments} />
            )}
          </ExpandSection>
        )}

        <ValuationSummarySection data={data} />

        {data.buildingPermitsCount > 0 && permits.length > 0 && (
          <ExpandSection
            label="Building permits"
            count={permits.length}
            preview={`Most recent: ${permits[0].subType} · ${formatMonthYear(
              permits[0].date
            )}`}
          >
            <VStack gap={0}>
              {permits.map((permit) => (
                <RecordItem key={permit.permitNumber}>
                  <RecordItemHead>
                    <RecordItemTitle>{permit.subType}</RecordItemTitle>
                    <RecordItemDate>
                      {formatLongDate(permit.date)}
                    </RecordItemDate>
                  </RecordItemHead>
                  <RecordItemBody>{permit.description}</RecordItemBody>
                  <RecordItemMeta>
                    {`Requestor: ${permit.requestor}${
                      permit.jobValue
                        ? ` · Job value ${money(permit.jobValue)}`
                        : ''
                    }`}
                  </RecordItemMeta>
                </RecordItem>
              ))}
            </VStack>
          </ExpandSection>
        )}

        {residents.length > 0 && (
          <ExpandSection
            label="Residents"
            count={residents.length}
            preview={`${
              residents.filter((resident) =>
                data.address
                  ? resident.currentAddress.includes(data.address.split(',')[0])
                  : false
              ).length
            } currently at this address`}
          >
            <ResidentsList
              residents={residents}
              onSendMessage={onSendMessage}
            />
          </ExpandSection>
        )}

        {data.deedsCount > 0 && deeds.length > 0 && (
          <ExpandSection
            label="Deeds"
            count={deeds.length}
            preview={`Latest: ${
              money(deeds[0].loans?.[0]?.loanAmount) ||
              money(deeds[0].transferValue) ||
              titleCase(deeds[0].transferType)
            } · ${formatMonthYear(deeds[0].recordingDate)}`}
          >
            <DeedsList deeds={deeds} />
          </ExpandSection>
        )}
      </VStack>
    </>
  );
};

// START NEXT_STEPS
export const NextStepsSection = ({
  data,
  onSendMessage,
}: {
  data: PropertyReportData;
  onSendMessage?: (message: string) => void;
}) => {
  const steps: string[] = [];
  if (hasValue(data.currentOwner)) {
    steps.push(`Who is ${titleCase(data.currentOwner)}?`);
  }
  steps.push('See neighborhood details');

  return (
    <>
      <SectionDivider />
      <NextStepsTitle>Possible next steps</NextStepsTitle>
      <VStack gap={0}>
        {steps.map((step) => (
          <NextStepButton
            key={step}
            type="button"
            onClick={() => onSendMessage?.(step)}
          >
            <SubdirectoryArrowRightIcon
              sx={{ fontSize: 16, color: 'text.primary' }}
            />
            <span>{step}</span>
          </NextStepButton>
        ))}
      </VStack>
    </>
  );
};
